/**
 * ============================================================
 * System       : CSAF Alert Finance
 * Objective    : Automated Reserve Fund Notification System
 * File         : frontend/src/pages/Settings.jsx
 * Description  : SMTP/LINE settings tabs
 * ------------------------------------------------------------
 * Developer    : Chokchai Thainkhao (Head of IT)
 * Co-Author    : AI Assisted (Claude) — โครงสร้างและฟังก์ชันหลัก
 * Modified &
 *  Integrated  : Chokchai Thainkhao
 * Version      : 1.0.0
 * Date         : May 2026
 * Copyright    : © 2026 Chokchai T. All rights reserved.
 * ============================================================
 */

import React, { useEffect, useState, useCallback } from 'react';
import { getSettings, saveSettings, testSmtp, testLine, testLineGrouped } from '../api';
import { useToast } from '../App';

const TABS = [
  { key: 'notif', label: 'การแจ้งเตือน' },
  { key: 'email', label: 'ทดสอบอีเมล' },
  { key: 'info',  label: 'ข้อมูลระบบ' },
];

function Card({ title, icon, children }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: 20 }}>
      <h3 style={{ fontSize: '.9rem', fontWeight: 700, color: '#111827', margin: '0 0 16px', display: 'flex', alignItems: 'center', gap: 8 }}>
        {icon && <span style={{ fontSize: '1.1rem' }}>{icon}</span>}
        {title}
      </h3>
      {children}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: 'block', fontSize: '.8rem', fontWeight: 600, color: '#374151', marginBottom: 5 }}>{label}</label>
      {children}
    </div>
  );
}

const inputCls = {
  width: '100%', border: '1px solid #d1d5db', borderRadius: 7, padding: '7px 11px',
  fontSize: '.85rem', outline: 'none', boxSizing: 'border-box', color: '#111827',
  fontFamily: 'inherit',
};

export default function Settings({ autoMode, setAutoMode }) {
  const [activeTab, setActiveTab] = useState('notif');
  const [settings, setSettings] = useState({
    smtp_host: 'smtp.gmail.com',
    smtp_port: '587',
    smtp_secure: 'false',
    smtp_from_name: '',
    smtp_user: '',
    smtp_pass: '',
    admin_email: 'admin@charoensin.co.th',
    line_notify:   '0',
    line_token:    '',
    line_group_id: '',
    auto_mode:     '0',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState('');
  const [testingLine, setTestingLine] = useState(false);
  const [testLineResult, setTestLineResult] = useState('');
  const [testingLineGrouped, setTestingLineGrouped] = useState(false);
  const [testLineGroupedResult, setTestLineGroupedResult] = useState('');
  const { addToast } = useToast();

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getSettings();
      setSettings((prev) => ({ ...prev, ...res.data }));
    } catch (e) {
      if (!e.response) {
        addToast('error', 'ไม่สามารถเชื่อมต่อเซิร์ฟเวอร์ได้');
      } else {
        addToast('error', e.response?.data?.detail || 'โหลดการตั้งค่าไม่สำเร็จ');
      }
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => { loadData(); }, [loadData]);

  const set = (key) => (e) =>
    setSettings((prev) => ({ ...prev, [key]: e.target.value }));

  const handleTestLine = async () => {
    setTestingLine(true);
    setTestLineResult('');
    try {
      const res = await testLine();
      const msg = res.data.message || 'ส่ง LINE ทดสอบสำเร็จ';
      setTestLineResult(`✅ ${msg}`);
      addToast('success', msg);
    } catch (err) {
      const msg = err.response?.data?.detail || 'ส่ง LINE ทดสอบไม่สำเร็จ';
      setTestLineResult(`❌ ${msg}`);
      addToast('error', msg);
    } finally {
      setTestingLine(false);
    }
  };

  const handleTestLineGrouped = async () => {
    setTestingLineGrouped(true);
    setTestLineGroupedResult('');
    try {
      const res = await testLineGrouped();
      const msg = res.data.message || 'ส่ง LINE ทดสอบแบบสรุปสำเร็จ';
      setTestLineGroupedResult(`✅ ${msg}`);
      addToast('success', msg);
    } catch (err) {
      const msg = err.response?.data?.detail || 'ส่ง LINE ทดสอบแบบสรุปไม่สำเร็จ';
      setTestLineGroupedResult(`❌ ${msg}`);
      addToast('error', msg);
    } finally {
      setTestingLineGrouped(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveSettings(settings);
      if (setAutoMode) setAutoMode(settings.auto_mode === '1');
      addToast('success', 'บันทึกการตั้งค่าสำเร็จ');
    } catch (e) {
      if (!e.response) {
        addToast('error', 'ไม่สามารถเชื่อมต่อเซิร์ฟเวอร์ได้');
      } else {
        addToast('error', e.response?.data?.detail || 'บันทึกการตั้งค่าไม่สำเร็จ');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleTestSmtp = async () => {
    if (!settings.smtp_host || !settings.smtp_user) {
      addToast('error', 'กรุณากรอก SMTP Host และ Email ผู้ส่งก่อนทดสอบ');
      return;
    }
    setTesting(true);
    setTestResult('');
    try {
      const res = await testSmtp();
      const msg = res.data.message || 'ส่งอีเมลทดสอบสำเร็จ';
      setTestResult(`✅ ${msg}`);
      addToast('success', msg);
    } catch (err) {
      if (!err.response) {
        const msg = 'ไม่สามารถเชื่อมต่อเซิร์ฟเวอร์ได้';
        setTestResult(`❌ ${msg}`);
        addToast('error', msg);
      } else {
        const msg = err.response?.data?.detail || 'ส่งอีเมลทดสอบไม่สำเร็จ';
        setTestResult(`❌ ${msg}`);
        addToast('error', msg);
      }
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="page-header" style={{ marginBottom: 0 }}>
        <h2 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#111827', margin: 0 }}>ตั้งค่าระบบ</h2>
      </div>

      {/* Tab bar */}
      <div style={{ display: 'flex', gap: 0, borderBottom: '2px solid #e5e7eb' }}>
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            style={{
              padding: '8px 20px',
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              fontSize: '.88rem',
              fontWeight: 500,
              color: activeTab === t.key ? '#1D9E75' : '#6b7280',
              borderBottom: activeTab === t.key ? '2px solid #1D9E75' : '2px solid transparent',
              marginBottom: -2,
              transition: 'color .15s, border-color .15s',
              fontFamily: 'inherit',
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab: การแจ้งเตือน */}
      {activeTab === 'notif' && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, maxWidth: 900 }}>
            {/* LINE Notify */}
            <Card title="LINE Notify" icon="💬">
              <Field label="LINE Channel Access Token">
                <input
                  style={inputCls}
                  type="text"
                  value={settings.line_token}
                  onChange={set('line_token')}
                  placeholder="กรอก Token จาก LINE Developers"
                />
              </Field>
              <Field label="LINE Group ID (ห้องรับแจ้งเตือน)">
                <input
                  style={inputCls}
                  type="text"
                  value={settings.line_group_id}
                  onChange={set('line_group_id')}
                  placeholder="เช่น C1234567890abcdef..."
                />
                <div style={{ fontSize: '.75rem', color: '#9ca3af', marginTop: 4 }}>
                  Group ID ของ LINE Group ที่ต้องการส่งการแจ้งเตือน
                </div>
              </Field>
              <Field label="สถานะ LINE Notify">
                <select style={inputCls} value={settings.line_notify} onChange={set('line_notify')}>
                  <option value="0">ปิดใช้งาน</option>
                  <option value="1">เปิดใช้งาน</option>
                </select>
              </Field>
              <button
                onClick={handleTestLine}
                disabled={testingLine}
                style={{
                  padding: '7px 16px', background: testingLine ? '#9ca3af' : '#06b6d4',
                  color: '#fff', border: 'none', borderRadius: 7, fontSize: '.83rem',
                  fontWeight: 600, cursor: testingLine ? 'not-allowed' : 'pointer',
                  fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 6,
                }}
              >
                {testingLine && (
                  <span style={{ width: 12, height: 12, border: '2px solid #fff', borderTopColor: 'transparent', borderRadius: '50%', display: 'inline-block', animation: 'spin 1s linear infinite' }} />
                )}
                💬 ทดสอบส่ง LINE
              </button>
              {testLineResult && (
                <div style={{ marginTop: 8, fontSize: '.82rem', color: testLineResult.startsWith('✅') ? '#1D9E75' : '#dc2626', wordBreak: 'break-all' }}>
                  {testLineResult}
                </div>
              )}

              <div style={{ borderTop: '1px dashed #e5e7eb', paddingTop: 12, marginTop: 4 }}>
                <div style={{ fontSize: '.78rem', color: '#6B7280', marginBottom: 8 }}>
                  ทดสอบรูปแบบใหม่ — แจ้งเตือนรวมรายบริษัท (จำลองข้อมูล 2 บริษัท × 3 คน)
                </div>
                <button
                  onClick={handleTestLineGrouped}
                  disabled={testingLineGrouped}
                  style={{
                    padding: '7px 16px', background: testingLineGrouped ? '#9ca3af' : '#7C3AED',
                    color: '#fff', border: 'none', borderRadius: 7, fontSize: '.83rem',
                    fontWeight: 600, cursor: testingLineGrouped ? 'not-allowed' : 'pointer',
                    fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 6,
                  }}
                >
                  {testingLineGrouped && (
                    <span style={{ width: 12, height: 12, border: '2px solid #fff', borderTopColor: 'transparent', borderRadius: '50%', display: 'inline-block', animation: 'spin 1s linear infinite' }} />
                  )}
                  📊 ทดสอบส่ง LINE แบบสรุปรายบริษัท
                </button>
                {testLineGroupedResult && (
                  <div style={{ marginTop: 8, fontSize: '.82rem', color: testLineGroupedResult.startsWith('✅') ? '#1D9E75' : '#dc2626', wordBreak: 'break-all' }}>
                    {testLineGroupedResult}
                  </div>
                )}
              </div>
            </Card>

            {/* SMTP Email */}
            <Card title="SMTP Email" icon="✉️">
              <Field label="SMTP Host">
                <input style={inputCls} type="text" value={settings.smtp_host} onChange={set('smtp_host')} placeholder="smtp.gmail.com" />
              </Field>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <Field label="Port">
                  <input style={inputCls} type="number" value={settings.smtp_port} onChange={set('smtp_port')} placeholder="587" />
                </Field>
                <Field label="Secure (TLS)">
                  <select style={inputCls} value={settings.smtp_secure} onChange={set('smtp_secure')}>
                    <option value="false">false (STARTTLS)</option>
                    <option value="true">true (SSL)</option>
                  </select>
                </Field>
              </div>
              <Field label="ชื่อผู้ส่ง">
                <input style={inputCls} type="text" value={settings.smtp_from_name} onChange={set('smtp_from_name')} placeholder="CSAF Alert Finance" />
              </Field>
              <Field label="Email ผู้ส่ง (username)">
                <input style={inputCls} type="text" value={settings.smtp_user} onChange={set('smtp_user')} placeholder="sender@example.com" />
              </Field>
              <Field label="รหัสผ่าน SMTP">
                <input style={inputCls} type="password" value={settings.smtp_pass} onChange={set('smtp_pass')} placeholder="App Password" autoComplete="new-password" />
              </Field>
            </Card>
          </div>

          <div style={{ marginTop: 20 }}>
            <button
              onClick={handleSave}
              disabled={saving}
              style={{
                padding: '8px 24px', background: saving ? '#9ca3af' : '#1D9E75',
                color: '#fff', border: 'none', borderRadius: 8, fontSize: '.88rem',
                fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
              }}
            >
              {saving ? 'กำลังบันทึก...' : '💾 บันทึกการตั้งค่า'}
            </button>
          </div>
        </div>
      )}

      {/* Tab: ทดสอบอีเมล */}
      {activeTab === 'email' && (
        <div style={{ maxWidth: 480 }}>
          <Card title="ทดสอบส่ง Email" icon="📧">
            <Field label="Email ปลายทาง">
              <input
                style={inputCls}
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="test@example.com"
              />
            </Field>
            <button
              onClick={handleTestSmtp}
              disabled={testing}
              style={{
                padding: '8px 20px', background: testing ? '#9ca3af' : '#1D9E75',
                color: '#fff', border: 'none', borderRadius: 8, fontSize: '.88rem',
                fontWeight: 600, cursor: testing ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
                display: 'flex', alignItems: 'center', gap: 8,
              }}
            >
              {testing && (
                <span style={{
                  width: 14, height: 14, border: '2px solid #fff', borderTopColor: 'transparent',
                  borderRadius: '50%', display: 'inline-block', animation: 'spin 1s linear infinite',
                }} />
              )}
              ส่งทดสอบ
            </button>
            {testResult && (
              <div style={{ marginTop: 14, fontSize: '.85rem', color: testResult.startsWith('✅') ? '#1D9E75' : '#dc2626' }}>
                {testResult}
              </div>
            )}
          </Card>
        </div>
      )}

      {/* Tab: ข้อมูลระบบ */}
      {activeTab === 'info' && (
        <div style={{ maxWidth: 680 }}>
          <Card title="ข้อมูลระบบ" icon="ℹ️">
            <div style={{ fontSize: '.83rem', color: '#6b7280', lineHeight: 1.6, display: 'flex', flexDirection: 'column', gap: 6 }}>

              {/* System info */}
              {[
                { label: 'ระบบ',     value: 'CSAF — Charoensin Alert Finance' },
                { label: 'เวอร์ชัน', value: '1.0.0' },
              ].map(({ label, value }) => (
                <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 10px', borderRadius: 7, background: '#F9FAFB' }}>
                  <span>{label}</span>
                  <strong style={{ color: '#111827' }}>{value}</strong>
                </div>
              ))}

              <div style={{ borderTop: '1px solid #E5E7EB', margin: '4px 0' }} />

              {/* Stack */}
              {[
                { icon: '🟢', label: 'Node.js',      version: 'v22.22.2',  desc: 'Runtime ฝั่ง Server' },
                { icon: '⚡', label: 'Express',      version: '4.18.2',    desc: 'Web Framework / API' },
                { icon: '🐘', label: 'PostgreSQL',   version: '18.4',      desc: 'ฐานข้อมูลหลัก' },
                { icon: '⚛️', label: 'React',        version: '18.2.0',    desc: 'UI Framework' },
                { icon: '🎨', label: 'Tailwind CSS', version: '3.3.0',     desc: 'CSS Styling' },
              ].map(({ icon, label, version, desc }) => (
                <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 10px', borderRadius: 7, background: '#F9FAFB' }}>
                  <span style={{ fontSize: 16, width: 22, textAlign: 'center', flexShrink: 0 }}>{icon}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <strong style={{ color: '#111827', fontSize: '.84rem' }}>{label}</strong>
                      <span style={{ background: '#E5E7EB', color: '#374151', fontSize: '.72rem', fontWeight: 700, padding: '1px 7px', borderRadius: 10 }}>{version}</span>
                    </div>
                    <div style={{ fontSize: '.75rem', color: '#9CA3AF', marginTop: 1 }}>{desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </Card>

          {/* Architecture Diagram */}
          <Card title="โครงสร้างระบบ" icon="🗺️">
            <div style={{ fontSize: '.8rem', fontFamily: 'inherit' }}>

              {/* Row helper */}
              {(() => {
                const Box = ({ icon, title, items, bg, border, titleColor }) => (
                  <div style={{ border: `1.5px solid ${border}`, borderRadius: 10, background: bg, padding: '10px 14px' }}>
                    <div style={{ fontWeight: 700, color: titleColor, marginBottom: 6, fontSize: '.82rem' }}>{icon} {title}</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                      {items.map((it, i) => (
                        <span key={i} style={{ background: 'rgba(255,255,255,0.7)', border: `1px solid ${border}`, borderRadius: 6, padding: '2px 9px', fontSize: '.75rem', color: '#374151' }}>{it}</span>
                      ))}
                    </div>
                  </div>
                );

                const Arrow = ({ label }) => (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', margin: '2px 0' }}>
                    <div style={{ width: 1.5, height: 12, background: '#9CA3AF' }} />
                    <div style={{ fontSize: '.7rem', color: '#9CA3AF', background: '#fff', padding: '0 6px', border: '1px solid #E5E7EB', borderRadius: 10, margin: '1px 0' }}>{label}</div>
                    <div style={{ width: 0, height: 0, borderLeft: '5px solid transparent', borderRight: '5px solid transparent', borderTop: '7px solid #9CA3AF' }} />
                  </div>
                );

                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>

                    {/* Browser */}
                    <Box icon="🖥️" title="Browser (ผู้ใช้งาน)" titleColor="#1D4ED8"
                      bg="#EFF6FF" border="#BFDBFE"
                      items={['React 18', 'Tailwind CSS 3', 'JWT Token (localStorage)']} />

                    <Arrow label="HTTPS Request / Response" />

                    {/* nginx */}
                    <Box icon="🔀" title="nginx — Reverse Proxy (Port 80)" titleColor="#374151"
                      bg="#F9FAFB" border="#D1D5DB"
                      items={['/ → React Static Files', '/api/* → Express :8000']} />

                    <Arrow label="Route ตาม Path" />

                    {/* Express */}
                    <div style={{ border: '1.5px solid #BBF7D0', borderRadius: 10, background: '#F0FDF4', padding: '10px 14px' }}>
                      <div style={{ fontWeight: 700, color: '#15803D', marginBottom: 8, fontSize: '.82rem' }}>⚡ Node.js / Express (Port 8000)</div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                        <div style={{ background: 'rgba(255,255,255,0.75)', border: '1px solid #BBF7D0', borderRadius: 7, padding: '7px 10px' }}>
                          <div style={{ fontWeight: 700, color: '#374151', marginBottom: 4, fontSize: '.75rem' }}>🔌 API Routes</div>
                          {['/records','/users','/companies','/settings','/history','/reports','/notifications'].map(r => (
                            <div key={r} style={{ fontSize: '.72rem', color: '#6B7280', lineHeight: 1.7 }}>{r}</div>
                          ))}
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          <div style={{ background: 'rgba(255,255,255,0.75)', border: '1px solid #BBF7D0', borderRadius: 7, padding: '7px 10px' }}>
                            <div style={{ fontWeight: 700, color: '#374151', marginBottom: 4, fontSize: '.75rem' }}>🔒 JWT Middleware</div>
                            <div style={{ fontSize: '.72rem', color: '#6B7280' }}>ตรวจสิทธิ์ทุก Request</div>
                            <div style={{ fontSize: '.72rem', color: '#6B7280' }}>แยก Admin / User</div>
                          </div>
                          <div style={{ background: 'rgba(255,255,255,0.75)', border: '1px solid #BBF7D0', borderRadius: 7, padding: '7px 10px' }}>
                            <div style={{ fontWeight: 700, color: '#374151', marginBottom: 4, fontSize: '.75rem' }}>⏰ Scheduler</div>
                            <div style={{ fontSize: '.72rem', color: '#6B7280' }}>cron ทุกวัน 08:00 น.</div>
                            <div style={{ fontSize: '.72rem', color: '#6B7280' }}>ส่ง Alert อัตโนมัติ</div>
                          </div>
                          <div style={{ background: 'rgba(255,255,255,0.75)', border: '1px solid #BBF7D0', borderRadius: 7, padding: '7px 10px' }}>
                            <div style={{ fontWeight: 700, color: '#374151', marginBottom: 4, fontSize: '.75rem' }}>📬 Mailer Service</div>
                            <div style={{ fontSize: '.72rem', color: '#6B7280' }}>SMTP Email</div>
                            <div style={{ fontSize: '.72rem', color: '#6B7280' }}>LINE Flex Message</div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <Arrow label="SQL Query" />

                    {/* PostgreSQL */}
                    <Box icon="🐘" title="PostgreSQL 18.4 — csafdb" titleColor="#92400E"
                      bg="#FFFBEB" border="#FDE68A"
                      items={['records','users','companies','user_companies','settings','history']} />

                    {/* External */}
                    <div style={{ marginTop: 16, borderTop: '1px dashed #E5E7EB', paddingTop: 12 }}>
                      <div style={{ fontSize: '.75rem', color: '#9CA3AF', marginBottom: 8 }}>External Services (Mailer เชื่อมต่อออก)</div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                        <div style={{ border: '1.5px solid #DDD6FE', borderRadius: 9, background: '#FAF5FF', padding: '8px 12px' }}>
                          <div style={{ fontWeight: 700, color: '#6D28D9', fontSize: '.78rem', marginBottom: 2 }}>✉️ Gmail SMTP</div>
                          <div style={{ fontSize: '.72rem', color: '#6B7280' }}>ส่งอีเมลแจ้งเตือน<br />ทีละคน พร้อม CC Admin</div>
                        </div>
                        <div style={{ border: '1.5px solid #BBF7D0', borderRadius: 9, background: '#F0FDF4', padding: '8px 12px' }}>
                          <div style={{ fontWeight: 700, color: '#15803D', fontSize: '.78rem', marginBottom: 2 }}>💬 LINE Messaging API</div>
                          <div style={{ fontSize: '.72rem', color: '#6B7280' }}>ส่ง Flex Message<br />1 ใบต่อรอบ รวมทุกบริษัท</div>
                        </div>
                      </div>
                    </div>

                  </div>
                );
              })()}
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
